import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";
import {
  type FontStyleId,
  FONT_STYLE_IDS,
  applyFontStyleToDocument,
  readStoredFontStyle,
  writeStoredFontStyle,
} from "@/lib/fontStyle";
import { normalizeBackendUrl } from "@/lib/api";

export type { FontStyleId };

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);
const TOKEN_KEY = "token";

interface FontStyleContextValue {
  fontStyle: FontStyleId;
  setFontStyle: (id: FontStyleId, options?: { persistRemote?: boolean }) => void;
}

const FontStyleContext = createContext<FontStyleContextValue>({
  fontStyle: "theme",
  setFontStyle: () => {},
});

export function FontStyleProvider({ children }: { children: React.ReactNode }) {
  const [fontStyle, setFontStyleState] = useState<FontStyleId>(() => readStoredFontStyle());

  useLayoutEffect(() => {
    applyFontStyleToDocument(fontStyle);
    writeStoredFontStyle(fontStyle);
  }, [fontStyle]);

  const setFontStyle = useCallback((id: FontStyleId, options?: { persistRemote?: boolean }) => {
    if (!(FONT_STYLE_IDS as readonly string[]).includes(id)) return;
    setFontStyleState(id);
    if (options?.persistRemote === false) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    fetch(`${BACKEND_URL}/api/user/preferences`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uiFontStyle: id }),
    }).catch(() => {});
  }, []);

  const value = useMemo(() => ({ fontStyle, setFontStyle }), [fontStyle, setFontStyle]);

  return (
    <FontStyleContext.Provider value={value}>
      {children}
    </FontStyleContext.Provider>
  );
}

export function useFontStyle() {
  return useContext(FontStyleContext);
}
